// Cost Analysis Tab — FR-2.1
// Cost breakdown table, savings opportunities, cost trend chart
import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  LinearProgress,
  Chip,
  Grid,
  Skeleton,
  Alert,
  alpha,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  TrendingDown as SavingsIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { TrendChart } from '../../../components/analytics';
import {
  useBomSavingsOpportunities,
  useBomCostTrend,
} from '../../../hooks/useBomIntelligence';
import { colors } from '../../../theme/analyticsColors';
import { getConfidenceColor } from '../utils/colors';
import type { CostIntelligenceLineItem, BomCostIntelligenceResponse } from '../../../types';

interface CostAnalysisTabProps {
  bomId: string;
  costData: BomCostIntelligenceResponse;
  onViewPart?: (partId: string) => void;
}

const formatCurrency = (value: number | undefined | null) => {
  if (value == null) return '—';
  if (Math.abs(value) < 0.01 && value !== 0) return `$${parseFloat(value.toPrecision(3))}`;
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCurrencyCompact = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

const CostAnalysisTab: React.FC<CostAnalysisTabProps> = ({ bomId, costData, onViewPart }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { data: savings, isLoading: savingsLoading } = useBomSavingsOpportunities(bomId, 5);
  const { data: trendData, isLoading: trendLoading } = useBomCostTrend(bomId);

  const lineItems = costData.lineItemCosts || [];

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totals = useMemo(() => {
    let totalUnitCost = 0;
    let totalBenchmark = 0;
    let savingsMin = 0;
    let savingsMax = 0;
    for (const item of lineItems) {
      totalUnitCost += (item.unitCost || 0) * (item.quantity || 1);
      const ci = item.costIntelligence;
      if (ci?.benchmarkPrice) totalBenchmark += ci.benchmarkPrice * (item.quantity || 1);
      if (ci?.savingsRange) {
        savingsMin += ci.savingsRange.min * (item.quantity || 1);
        savingsMax += ci.savingsRange.max * (item.quantity || 1);
      }
    }
    return { totalUnitCost, totalBenchmark, savingsMin, savingsMax };
  }, [lineItems]);

  const trendChartData = useMemo(() => {
    if (!trendData?.dataPoints) return [];
    return trendData.dataPoints.map(dp => ({
      date: new Date(dp.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      'Total BOM Cost': dp.totalBOMCost,
      'Material Cost': dp.totalMaterialCost,
      'Labor Cost': dp.totalLaborCost,
    }));
  }, [trendData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Cost Breakdown Table */}
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.border}` }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: colors.primary }}>
              Cost Breakdown by Line Item
            </Typography>
            <Typography variant="body2" sx={{ color: colors.secondary, mt: 0.5 }}>
              {costData.enrichedCount} of {costData.lineItemCount} items enriched
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>#</TableCell>
                  <TableCell>Part Number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Should Cost</TableCell>
                  <TableCell align="right">Benchmark</TableCell>
                  <TableCell align="right">Savings</TableCell>
                  <TableCell sx={{ width: 120 }}>Confidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item: CostIntelligenceLineItem, idx: number) => {
                  const ci = item.costIntelligence;
                  const isExpanded = expandedRows.has(item.lineItemId);
                  const isAboveBenchmark = ci?.benchmarkPrice && item.unitCost > ci.benchmarkPrice * 1.15;

                  return (
                    <React.Fragment key={item.lineItemId}>
                      <TableRow
                        hover
                        sx={{
                          bgcolor: isAboveBenchmark
                            ? alpha(colors.error, 0.04)
                            : idx % 2 === 0
                              ? 'transparent'
                              : alpha(colors.surfaceAlt, 0.5),
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleRow(item.lineItemId)}
                      >
                        <TableCell sx={{ p: 0.5 }}>
                          <IconButton size="small">
                            {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ color: colors.secondary, fontSize: '0.8125rem' }}>{item.lineNumber}</TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: onViewPart && item.partId ? colors.chart.primary : colors.primary,
                            ...(onViewPart && item.partId && {
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }),
                          }}
                          onClick={(e) => {
                            if (onViewPart && item.partId) {
                              e.stopPropagation();
                              onViewPart(item.partId);
                            }
                          }}
                        >
                          {item.partNumber}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', color: colors.primary, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.partDescription || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8125rem', color: colors.primary }}>{item.quantity}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8125rem', color: colors.primary }}>
                          {formatCurrency(item.unitCost)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8125rem', color: ci?.shouldCost ? colors.success : colors.light }}>
                          {formatCurrency(ci?.shouldCost)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8125rem', color: ci?.benchmarkPrice ? colors.chart.primary : colors.light }}>
                          {formatCurrency(ci?.benchmarkPrice)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8125rem' }}>
                          {ci?.savingsRange ? (
                            <Typography component="span" sx={{ color: colors.success, fontWeight: 500, fontSize: '0.8125rem' }}>
                              {formatCurrency(ci.savingsRange.min)} – {formatCurrency(ci.savingsRange.max)}
                            </Typography>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {ci ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={ci.confidence * 100}
                                sx={{
                                  flex: 1,
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: colors.surfaceAlt,
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: getConfidenceColor(ci.confidence),
                                    borderRadius: 3,
                                  },
                                }}
                              />
                              <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, minWidth: 32 }}>
                                {Math.round(ci.confidence * 100)}%
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: colors.light }}>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Details */}
                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0, borderBottom: isExpanded ? `1px solid ${colors.border}` : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto">
                            <Box sx={{ p: 2, pl: 7, bgcolor: colors.surfaceAlt }}>
                              <Grid container spacing={3}>
                                <Grid size={{ xs: 4 }}>
                                  <Typography variant="caption" sx={{ color: colors.secondary, textTransform: 'uppercase', fontWeight: 600 }}>
                                    Source
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                                    {ci?.source || 'Not enriched'}
                                  </Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                  <Typography variant="caption" sx={{ color: colors.secondary, textTransform: 'uppercase', fontWeight: 600 }}>
                                    Last Updated
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                                    {ci?.lastUpdated
                                      ? new Date(ci.lastUpdated).toLocaleDateString()
                                      : 'Never'}
                                  </Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                  <Typography variant="caption" sx={{ color: colors.secondary, textTransform: 'uppercase', fontWeight: 600 }}>
                                    Extended Cost
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                                    {formatCurrency(item.totalCost || item.unitCost * item.quantity)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}

                {/* Totals Footer */}
                <TableRow sx={{ bgcolor: colors.surfaceAlt }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 600, color: colors.primary }}>Totals</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: colors.primary }}>
                    {formatCurrency(totals.totalUnitCost)}
                  </TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: colors.chart.primary }}>
                    {totals.totalBenchmark > 0 ? formatCurrency(totals.totalBenchmark) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: colors.success }}>
                    {totals.savingsMax > 0
                      ? `${formatCurrency(totals.savingsMin)} – ${formatCurrency(totals.savingsMax)}`
                      : '—'}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Bottom Row: Savings + Trend */}
      <Grid container spacing={3}>
        {/* Savings Opportunities */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SavingsIcon sx={{ color: colors.success, fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: colors.primary }}>
                  Top Savings Opportunities
                </Typography>
              </Box>
              {savingsLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[1, 2, 3].map(i => <Skeleton key={i} height={48} />)}
                </Box>
              ) : savings && savings.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {savings.map((opp) => (
                    <Box
                      key={opp.lineItemId}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(colors.success, 0.04),
                        border: '1px solid',
                        borderColor: alpha(colors.success, 0.12),
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8125rem', fontWeight: 500, color: colors.primary }}>
                          {opp.partNumber}
                        </Typography>
                        <Chip
                          label={formatCurrency(opp.totalPotentialSavings)}
                          size="small"
                          sx={{
                            bgcolor: alpha(colors.success, 0.1),
                            color: colors.success,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', color: colors.secondary }}>
                        {opp.recommendation}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.875rem', color: colors.light, textAlign: 'center', py: 3 }}>
                  No savings opportunities found. Run cost enrichment first.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cost Trend Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MoneyIcon sx={{ color: colors.chart.primary, fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: colors.primary }}>
                  Cost Trend
                </Typography>
                {trendData?.trend && (
                  <Chip
                    label={trendData.trend}
                    size="small"
                    sx={{
                      ml: 'auto',
                      bgcolor: trendData.trend === 'decreasing'
                        ? alpha(colors.success, 0.1)
                        : trendData.trend === 'increasing'
                          ? alpha(colors.error, 0.1)
                          : alpha(colors.secondary, 0.1),
                      color: trendData.trend === 'decreasing'
                        ? colors.success
                        : trendData.trend === 'increasing'
                          ? colors.error
                          : colors.secondary,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      textTransform: 'capitalize',
                    }}
                  />
                )}
              </Box>
              {trendLoading ? (
                <Skeleton height={280} variant="rectangular" sx={{ borderRadius: 1 }} />
              ) : trendChartData.length > 0 ? (
                <TrendChart
                  data={trendChartData}
                  lines={[
                    { dataKey: 'Total BOM Cost', color: colors.chart.primary, name: 'Total BOM Cost', areaFill: true },
                    { dataKey: 'Material Cost', color: colors.chart.success, name: 'Material Cost' },
                    { dataKey: 'Labor Cost', color: colors.chart.warning, name: 'Labor Cost', dashed: true },
                  ]}
                  formatYAxis={(v: number) => formatCurrencyCompact(v)}
                  formatTooltip={(v: number, name: string) => [formatCurrency(v), name]}
                  height={280}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                  <Typography sx={{ color: colors.light }}>
                    No trend data available. Run cost enrichment to generate snapshots.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CostAnalysisTab;
